import { TriangleAlert } from 'lucide-react';
import { useApiProblem } from '@/shared/api';
import { Button, EmptyState } from '@/shared/components';

const card = 'border-hairline rounded-tile border bg-white';

/** Laden: Platzhalter in Kartenform statt leerer Fläche. */
export function BookingsListLoading() {
  return (
    <div
      role="status"
      aria-label="Buchungen werden geladen"
      className="flex animate-pulse flex-col gap-2.5"
    >
      {Array.from({ length: 4 }, (_, index) => (
        <div key={index} className={`${card} h-[76px]`} />
      ))}
    </div>
  );
}

/**
 * Ladefehler mit dem Grund des Servers. Ein 4xx kommt meist von einer alten oder von Hand
 * geänderten Adresse – dann hilft „Filter zurücksetzen“, ein neuer Versuch nicht.
 */
export function BookingsListError({
  error,
  onRetry,
  retrying,
  onReset,
}: {
  error: unknown;
  onRetry: () => void;
  retrying: boolean;
  onReset?: () => void;
}) {
  const problem = useApiProblem(error);
  const client = problem !== null && problem.status >= 400 && problem.status < 500;
  const reason = !problem
    ? null
    : problem.status === 0
      ? 'Keine Verbindung.'
      : (problem.detail ??
        (problem.status >= 500
          ? `Der Server meldet einen Fehler (${problem.status}).`
          : `Code ${problem.title}`));
  return (
    <div role="alert" className={`${card} flex flex-wrap items-center gap-3 p-4`}>
      <TriangleAlert size={18} aria-hidden className="text-amber flex-none" />
      <p className="min-w-0 flex-1 text-sm">
        Die Buchungen konnten nicht geladen werden.{reason ? ` ${reason}` : ''}
      </p>
      {client && onReset ? (
        <Button variant="secondary" onClick={onReset}>
          Filter zurücksetzen
        </Button>
      ) : (
        <Button variant="secondary" onClick={onRetry} loading={retrying}>
          Erneut versuchen
        </Button>
      )}
    </div>
  );
}

/** Ohne Apartmentliste fehlen Namen, Filter und die Auswahl der manuellen Buchung – das muss dastehen. */
export function ApartmentsUnavailable({
  onRetry,
  retrying,
}: {
  onRetry: () => void;
  retrying: boolean;
}) {
  return (
    <div role="alert" className={`${card} flex flex-wrap items-center gap-3 p-4`}>
      <TriangleAlert size={18} aria-hidden className="text-amber flex-none" />
      <p className="min-w-0 flex-1 text-sm">
        Die Apartments konnten nicht geladen werden – Namen, Apartment-Filter und die manuelle
        Buchung fehlen so lange.
      </p>
      <Button variant="secondary" onClick={onRetry} loading={retrying}>
        Erneut laden
      </Button>
    </div>
  );
}

/**
 * Keine Treffer: gefiltert mit Zurücksetzen, „ab heute“ mit dem Weg zu den Vergangenen, sonst
 * der Hinweis, woher Buchungen kommen.
 */
export function BookingsListEmpty({
  filtered,
  showPast,
  onReset,
  onShowPast,
}: {
  filtered: boolean;
  showPast: boolean;
  onReset: () => void;
  onShowPast: () => void;
}) {
  if (filtered) {
    return (
      <div className={`${card} flex flex-col items-center pb-6`}>
        <EmptyState
          icon="CalendarDays"
          title="Keine Buchungen für diese Filter"
          description={
            showPast
              ? 'Suche und Filter zusammen ergeben keinen Treffer.'
              : 'Gesucht wurde ab heute – vergangene Buchungen sind ausgeblendet.'
          }
          action={
            showPast
              ? { label: 'Filter zurücksetzen', onClick: onReset }
              : { label: 'Vergangene zeigen', onClick: onShowPast }
          }
        />
        {!showPast && (
          <Button variant="ghost" onClick={onReset}>
            Filter zurücksetzen
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className={card}>
      <EmptyState
        icon="CalendarDays"
        title={showPast ? 'Noch keine Buchungen' : 'Ab heute keine Buchungen'}
        description={
          showPast
            ? 'Sobald eine Reservierung über einen verbundenen Kanal eintrifft oder Sie eine anlegen, erscheint sie hier.'
            : 'Niemand ist im Haus, reist heute ab oder kommt noch.'
        }
        action={showPast ? undefined : { label: 'Vergangene zeigen', onClick: onShowPast }}
      />
    </div>
  );
}
